import { useCallback, useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  AlertTriangle,
  Ban,
  BarChart3,
  Building2,
  Clock,
  ClipboardList,
  Database,
  Download,
  Eye,
  FileText,
  Info,
  LayoutTemplate,
  Lightbulb,
  Mail,
  PauseCircle,
  Send,
  Settings2,
  ShieldAlert,
  Sparkles,
  UserCog,
  UserPlus,
} from "lucide-react";
import {
  createRole,
  deleteRole,
  fetchAdminUsers,
  fetchPermissionRegistry,
  fetchRoles,
  updateRole,
} from "../api/adminClient";
import AdminConsoleTabs from "./AdminConsoleTabs";

const emptyForm = { name: "", description: "", permissions: [] };

// Plain-language copy + icon + access tier for every permission in the
// registry. Falls back to the raw group label if a key isn't mapped here
// (keeps the UI from breaking silently if the backend registry grows).
const PERMISSION_META = {
  "companies:read": {
    icon: Eye,
    header: "View Companies",
    description: "See the list of companies and their basic details, without making changes.",
    impact: "read",
  },
  "companies:write": {
    icon: Building2,
    header: "Edit Company Profiles",
    description: "Create new companies and update existing company information.",
    impact: "standard",
  },
  "companies:manage": {
    icon: PauseCircle,
    header: "Pause / Unpause Companies",
    description: "Temporarily suspend a company's access to the platform, or restore it.",
    impact: "sensitive",
  },
  "invites:read": {
    icon: Eye,
    header: "View Invites & Participants",
    description: "See invite status and participant progress, without sending or editing anything.",
    impact: "read",
  },
  "invites:write": {
    icon: UserPlus,
    header: "Create Invites",
    description: "Add new participants individually or upload them in bulk.",
    impact: "standard",
  },
  "invites:send": {
    icon: Send,
    header: "Send & Resend Invites",
    description: "Allows the user to invite participants and trigger email assessment links.",
    impact: "standard",
  },
  "invites:disable": {
    icon: Ban,
    header: "Disable Invites",
    description: "Revoke a participant's ability to access their assessment link.",
    impact: "standard",
  },
  "invites:manage": {
    icon: Clock,
    header: "Configure Reminder Emails",
    description: "Set up and adjust the automatic reminder schedule sent to participants.",
    impact: "standard",
  },
  "assessment_form:manage": {
    icon: ClipboardList,
    header: "Edit Assessment Questions",
    description: "Customize survey questions and choose the shortened LITE version.",
    impact: "standard",
  },
  "templates:manage": {
    icon: Mail,
    header: "Manage Email Templates",
    description: "View, create, and edit the email templates used for invites and reminders.",
    impact: "standard",
  },
  "reports:read": {
    icon: FileText,
    header: "View & Generate Reports",
    description: "Open participant reports and generate downloadable PDFs.",
    impact: "read",
  },
  "reports:export": {
    icon: Download,
    header: "Export Survey Responses",
    description: "Download raw survey response data for offline analysis.",
    impact: "standard",
  },
  "report_profiles:read": {
    icon: LayoutTemplate,
    header: "View Report Profiles",
    description: "See which report layout is assigned to each company.",
    impact: "read",
  },
  "report_profiles:manage": {
    icon: Settings2,
    header: "Manage Report Profiles",
    description: "Create, edit, and assign report profiles to companies.",
    impact: "standard",
  },
  "analytics:read": {
    icon: BarChart3,
    header: "View Analytics",
    description: "Access dashboards showing engagement and completion trends.",
    impact: "read",
  },
  "insights:read": {
    icon: Lightbulb,
    header: "View AI Insights",
    description: "Read AI-generated narratives and summaries about participant results.",
    impact: "read",
  },
  "ai_copilot:use": {
    icon: Sparkles,
    header: "Use AI Copilot",
    description: "Chat with the built-in AI assistant to help navigate admin tasks.",
    impact: "standard",
  },
  "platform:manage": {
    icon: Database,
    header: "Manage Platform Tools",
    description: "Configure the knowledge base and connected technical integrations.",
    impact: "standard",
  },
  "admin:manage_users": {
    icon: UserCog,
    header: "Manage Admin Users",
    description: "Create new admin accounts, reset passwords, and assign roles to others.",
    impact: "sensitive",
  },
  "admin:manage_roles": {
    icon: ShieldAlert,
    header: "Create & Delete Roles",
    description: "Define new roles or permanently delete existing ones, including this one.",
    impact: "sensitive",
  },
};

const IMPACT_LABELS = {
  read: "Read Only",
  standard: "Standard Access",
  sensitive: "High Sensitivity",
};

const IMPACT_STYLES = {
  read: "bg-slate-100 text-slate-600",
  standard: "bg-sky-50 text-sky-700",
  sensitive: "bg-red-100 text-red-700",
};

const ROLE_TEMPLATES = [
  {
    key: "company_admin",
    label: "Company Admin",
    description: "Manage companies, invites, and view reports & analytics.",
    permissions: [
      "companies:read",
      "companies:write",
      "invites:read",
      "invites:write",
      "invites:send",
      "invites:disable",
      "invites:manage",
      "reports:read",
      "reports:export",
      "report_profiles:read",
      "analytics:read",
      "insights:read",
      "ai_copilot:use",
    ],
  },
  {
    key: "report_viewer",
    label: "Report Viewer",
    description: "Read-only access to companies, invites, reports, and analytics.",
    permissions: [
      "companies:read",
      "invites:read",
      "reports:read",
      "report_profiles:read",
      "analytics:read",
      "insights:read",
    ],
  },
  {
    key: "email_manager",
    label: "Email Manager",
    description: "Send invites, manage reminders, and edit email templates.",
    permissions: [
      "invites:read",
      "invites:write",
      "invites:send",
      "invites:disable",
      "invites:manage",
      "templates:manage",
    ],
  },
  {
    key: "custom",
    label: "Custom",
    description: "Start from a blank slate and choose permissions yourself.",
    permissions: [],
  },
];

function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function InfoTooltip({ text }) {
  return (
    <span className="group/tip relative inline-flex">
      <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help" aria-hidden />
      <span className="sr-only">{text}</span>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-[10px] font-medium text-white opacity-0 shadow-lg transition-opacity group-hover/tip:opacity-100 group-focus-within/tip:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

function ImpactTag({ impact }) {
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        IMPACT_STYLES[impact] || IMPACT_STYLES.standard
      }`}
    >
      {IMPACT_LABELS[impact] || "Standard Access"}
    </span>
  );
}

function ToggleSwitch({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/40 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 ${
        checked ? "bg-[#004f73]" : "bg-slate-300"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function PermissionCard({ perm, checked, disabled, onToggle, warning }) {
  const meta = PERMISSION_META[perm.key] || {
    icon: Info,
    header: perm.label,
    description: "",
    impact: "standard",
  };
  const Icon = meta.icon;
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${
        warning
          ? "border-red-200 bg-red-50/60"
          : checked
          ? "border-[#004f73]/30 bg-[#004f73]/[0.04]"
          : "border-slate-200 bg-slate-50"
      }`}
    >
      <div
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${
          warning
            ? "border-red-200 bg-white text-red-600"
            : "border-slate-200 bg-white text-[#004f73]"
        }`}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-semibold text-gray-800">{meta.header}</h4>
          <InfoTooltip text={perm.key} />
        </div>
        {meta.description ? (
          <p className="mt-0.5 text-xs leading-snug text-slate-500">{meta.description}</p>
        ) : null}
        <div className="mt-2">
          <ImpactTag impact={meta.impact} />
        </div>
      </div>
      <ToggleSwitch checked={checked} disabled={disabled} onChange={onToggle} label={meta.header} />
    </div>
  );
}

function CategorySection({ group, formPermissions, isProtected, onTogglePermission, onToggleGroup }) {
  const keys = group.permissions.map((p) => p.key);
  const selectedCount = keys.filter((k) => formPermissions.includes(k)).length;
  const allChecked = keys.length > 0 && selectedCount === keys.length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-gray-900">{group.label}</h3>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
            {selectedCount}/{keys.length} selected
          </span>
        </div>
        {!isProtected ? (
          <button
            type="button"
            onClick={() => onToggleGroup(group, !allChecked)}
            className="text-xs font-semibold text-[#004f73] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/30 rounded"
          >
            {allChecked ? "Clear all" : "Select all"}
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {group.permissions.map((perm) => (
          <PermissionCard
            key={perm.key}
            perm={perm}
            checked={formPermissions.includes(perm.key)}
            disabled={isProtected}
            onToggle={() => onTogglePermission(perm.key)}
          />
        ))}
      </div>
    </div>
  );
}

export default function RoleManagementPage() {
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // role object | null
  const [deleting, setDeleting] = useState(false);
  const [deleteUserCount, setDeleteUserCount] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [permRes, roleRes] = await Promise.all([fetchPermissionRegistry(), fetchRoles()]);
      setGroups(permRes.data?.groups || []);
      setRoles(roleRes.data?.roles || []);
    } catch (err) {
      toast.error(err.message || "Could not load roles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!formOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setFormOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [formOpen]);

  const allPermissionKeys = useMemo(
    () => groups.flatMap((g) => g.permissions.map((p) => p.key)),
    [groups]
  );

  // High-sensitivity permissions are pulled out of their normal category
  // and rendered in one dedicated, visually-flagged section instead —
  // keeps destructive actions from blending in with routine ones.
  const { normalGroups, sensitivePerms } = useMemo(() => {
    const sensitive = [];
    const normal = groups
      .map((g) => {
        const perms = g.permissions.filter((p) => {
          if (PERMISSION_META[p.key]?.impact === "sensitive") {
            sensitive.push(p);
            return false;
          }
          return true;
        });
        return { ...g, permissions: perms };
      })
      .filter((g) => g.permissions.length > 0);
    return { normalGroups: normal, sensitivePerms: sensitive };
  }, [groups]);

  const accessSummary = useMemo(() => {
    const total = allPermissionKeys.length;
    const granted = form.permissions.length;
    const hasSensitive = form.permissions.some((k) => PERMISSION_META[k]?.impact === "sensitive");
    const hasStandard = form.permissions.some((k) => PERMISSION_META[k]?.impact === "standard");
    let level = "No Access";
    if (granted > 0) {
      level = hasSensitive ? "Full Administrative" : hasStandard ? "Standard Access" : "Read Only";
    }
    return { total, granted, level };
  }, [form.permissions, allPermissionKeys]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSelectedTemplate(null);
    setNameTouched(false);
    setFormOpen(true);
  };

  const openEdit = (role) => {
    setEditingId(role.id);
    setForm({ name: role.name, description: role.description, permissions: [...role.permissions] });
    setSelectedTemplate(null);
    setNameTouched(false);
    setFormOpen(true);
  };

  const applyTemplate = (tpl) => {
    setSelectedTemplate(tpl.key);
    setForm((f) => ({ ...f, permissions: [...tpl.permissions] }));
  };

  const togglePermission = (key) => {
    setSelectedTemplate(null);
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const toggleGroup = (group, checkAll) => {
    const keys = group.permissions.map((p) => p.key);
    setSelectedTemplate(null);
    setForm((f) => ({
      ...f,
      permissions: checkAll
        ? Array.from(new Set([...f.permissions, ...keys]))
        : f.permissions.filter((p) => !keys.includes(p)),
    }));
  };

  const editingRole = editingId ? roles.find((r) => r.id === editingId) : null;
  const isProtected = !!editingRole?.is_protected;

  const handleSave = async (e) => {
    e.preventDefault();
    setNameTouched(true);
    if (!form.name.trim()) {
      toast.error("Role name is required.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateRole(editingId, {
          name: form.name.trim(),
          description: form.description,
          permissions: form.permissions,
        });
        toast.success("Role updated.");
      } else {
        await createRole({
          name: form.name.trim(),
          description: form.description,
          permissions: form.permissions,
        });
        toast.success("Role created.");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(err.message || "Could not save role.");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = async (role) => {
    if (role.is_protected) return;
    setDeleteTarget(role);
    setDeleteUserCount(null);
    // Fetch user count asynchronously so it can show in the dialog
    try {
      const res = await fetchAdminUsers();
      const allUsers = res.data?.users || [];
      setDeleteUserCount(allUsers.filter((u) => u.role_id === role.id).length);
    } catch {
      setDeleteUserCount(0);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRole(deleteTarget.id);
      toast.success("Role deleted.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.message || "Could not delete role.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-full w-full bg-white">
      <Toaster position="top-center" />
      <div className="w-full px-6 py-6">
        <header className="mb-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004f73]/70 mb-2">
              Admin
            </p>
            <h1 className="text-2xl font-bold text-gray-900">Roles &amp; Permissions</h1>
            <p className="text-sm text-slate-500 mt-1">
              Define custom roles and control what each one can see and do across companies.
            </p>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg transition-colors"
            onClick={openCreate}
          >
            New role
          </button>
        </header>
        <AdminConsoleTabs active="roles" />

        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#004f73]">
            <Spinner />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {roles.map((role) => (
              <div
                key={role.id}
                className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{role.name}</span>
                    {role.is_protected ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-[#004f73] bg-[#004f73]/10 rounded-full px-2 py-0.5">
                        Built-in
                      </span>
                    ) : null}
                  </div>
                  {role.description ? (
                    <p className="text-sm text-slate-500 truncate">{role.description}</p>
                  ) : null}
                  <p className="text-xs text-slate-400 mt-1">
                    {role.permissions.length} of {allPermissionKeys.length} permissions
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-[#004f73] border border-[#004f73]/30 rounded-lg hover:bg-[#004f73]/5"
                    onClick={() => openEdit(role)}
                  >
                    Edit
                  </button>
                  {!role.is_protected ? (
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                      onClick={() => openDeleteDialog(role)}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {formOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-modal-title"
            className="w-full max-w-4xl max-h-[88vh] overflow-y-auto rounded-2xl bg-white p-6 sm:p-7 shadow-xl"
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <h2 id="role-modal-title" className="text-lg font-bold text-gray-900">
                {editingId ? `Edit role — ${editingRole?.name || ""}` : "New role"}
              </h2>
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                aria-label="Close"
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/30"
              >
                ✕
              </button>
            </div>

            {isProtected ? (
              <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">This is a built-in role.</span> The name and
                  admin-management permissions are locked to prevent accidental lock-outs. You can
                  still edit the description.
                </p>
              </div>
            ) : null}

            {/* Live preview — total granted permissions and overall access level */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Granted permissions
                </p>
                <p className="text-lg font-bold text-gray-900">
                  {accessSummary.granted}
                  <span className="text-sm font-medium text-slate-400"> of {accessSummary.total}</span>
                </p>
              </div>
              <span
                className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${
                  accessSummary.level === "Full Administrative"
                    ? "bg-red-100 text-red-700"
                    : accessSummary.level === "Standard Access"
                    ? "bg-sky-100 text-sky-700"
                    : accessSummary.level === "Read Only"
                    ? "bg-slate-200 text-slate-600"
                    : "bg-slate-100 text-slate-400"
                }`}
              >
                {accessSummary.level}
              </span>
            </div>

            <form onSubmit={handleSave} className="space-y-6">
              {!editingId ? (
                <div>
                  <p className="mb-1 text-sm font-semibold text-gray-800">Starter templates</p>
                  <p className="mb-3 text-xs text-slate-500">
                    Pick a starting point — you can still fine-tune every permission below.
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {ROLE_TEMPLATES.map((tpl) => (
                      <button
                        key={tpl.key}
                        type="button"
                        onClick={() => applyTemplate(tpl)}
                        title={tpl.description}
                        aria-pressed={selectedTemplate === tpl.key}
                        className={`rounded-xl border px-3 py-2.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/30 ${
                          selectedTemplate === tpl.key
                            ? "border-[#004f73] bg-[#004f73]/5 ring-1 ring-[#004f73]/30"
                            : "border-slate-200 hover:border-[#004f73]/40 hover:bg-slate-50"
                        }`}
                      >
                        <span className="block text-sm font-semibold text-gray-800">{tpl.label}</span>
                        <span className="mt-0.5 block text-[11px] leading-snug text-slate-500 line-clamp-2">
                          {tpl.description}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Name</label>
                  <input
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                      nameTouched && !form.name.trim()
                        ? "border-red-300 focus:ring-red-200"
                        : "border-slate-200 focus:ring-[#004f73]/20"
                    }`}
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    onBlur={() => setNameTouched(true)}
                    disabled={isProtected}
                    required
                  />
                  {nameTouched && !form.name.trim() ? (
                    <p className="mt-1 text-xs text-red-600">Role name is required.</p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Description</label>
                  <input
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#004f73]/20"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">Permissions</p>
                {isProtected ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                    This is the built-in Super Admin role — admin management permissions can&apos;t be
                    removed, to avoid locking everyone out.
                  </p>
                ) : null}
                <div className="space-y-3">
                  {normalGroups.map((group) => (
                    <CategorySection
                      key={group.key}
                      group={group}
                      formPermissions={form.permissions}
                      isProtected={isProtected}
                      onTogglePermission={togglePermission}
                      onToggleGroup={toggleGroup}
                    />
                  ))}

                  {sensitivePerms.length > 0 ? (
                    <div className="rounded-2xl border-2 border-red-200 bg-red-50/50 p-4">
                      <div className="mb-1 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-600" aria-hidden />
                        <h3 className="text-sm font-bold text-red-800">High Sensitivity / Admin</h3>
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                          {sensitivePerms.filter((p) => form.permissions.includes(p.key)).length}/
                          {sensitivePerms.length} selected
                        </span>
                      </div>
                      <p className="mb-3 text-xs text-red-700">
                        These permissions can affect other admins or take destructive, hard-to-reverse
                        actions. Grant with care.
                      </p>
                      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        {sensitivePerms.map((perm) => (
                          <PermissionCard
                            key={perm.key}
                            perm={perm}
                            checked={form.permissions.includes(perm.key)}
                            disabled={isProtected}
                            onToggle={() => togglePermission(perm.key)}
                            warning
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg"
                  onClick={() => setFormOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-role-title"
            className="w-full max-w-md rounded-[20px] border border-slate-200 bg-white p-7 shadow-[0_24px_60px_-12px_rgba(0,79,115,0.4)] dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="mb-4 flex items-start gap-3.5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-400">
                <AlertTriangle className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <h2 id="delete-role-title" className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  Delete role &ldquo;{deleteTarget.name}&rdquo;?
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            {deleteUserCount !== null ? (
              <p className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                {deleteUserCount > 0
                  ? `${deleteUserCount} admin${deleteUserCount === 1 ? "" : "s"} currently assigned to this role will lose their access level and must be reassigned.`
                  : "No admins are currently assigned to this role."}
              </p>
            ) : (
              <p className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-xs text-slate-400">
                Checking affected admins…
              </p>
            )}
            <div className="flex items-center justify-end gap-2.5">
              <button
                type="button"
                className="rounded-full px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-full bg-gradient-to-b from-red-500 to-red-600 px-5 py-2.5 text-sm font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_3px_10px_-2px_rgba(190,18,60,0.5)] transition-all hover:from-red-500 hover:to-red-700 active:translate-y-px disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Delete role"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
